import React, {useCallback, useEffect, useState} from "react";
import {DialogTitle, Divider} from "@mui/material";
import Dialog from "../../../base/dialog";
import AssignedItemsList from "./sections/list";
import AddAssignedItems from "./sections/add";
import Api from "../../../../../core/services/api/api";
import useAccessLevels from "../../../../hooks/use-access-levels";

const ManageProductTechnicFilesAssignedItemsDialog = ({open, close, technicalFile, industryId}) => {
    const [inAddMode, setInAddMode] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const {packages} = useAccessLevels();
    const canEditTechnicalFiles = packages.lookup.editProductTechnicalFiles;

    /**
     * With each change in open:
     * - if closed, then resets inAddMode state.
     */
    useEffect(() => {
        if (!open) {
            setInAddMode(false);
        }
    }, [open])

    /**
     * Assigns the given items to the current technical file in the server.
     * @param items
     * @return {Promise<ApiResponseDs<*>|undefined>}
     */
    const assignItemToTechnicalFile = async (items) => {
        if (!canEditTechnicalFiles) return {resultFlag: false};
        const forApi = {
            selectedDocument: technicalFile.id,
            partNos: items?.records?.map(e => e.partNo ?? e.partNumber) ?? [],
            categoryId: items?.category?.id,
        }
        setDisabled(true);
        const response = await Api.assignPartsToOperatorPartTechnicalFile(forApi);
        setDisabled(false);
        return response;
    }

    /**
     * Un-assigns the selected items from the current technical file.
     * @param items
     * @return {Promise<{resultFlag: boolean}>}
     */
    const unAssignItemsFromTechnicalFile = useCallback(async (items) => {
        if (!canEditTechnicalFiles) return {resultFlag: false};
        const forApi = {
            documentId: technicalFile.id,
            ids: items?.records?.map(e => e.id) ?? [],
            parentCategoryId: items?.category?.id ?? null,
        }
        setDisabled(true);
        const response = await Api.unAssignItemsFromTechnicalFile(forApi);
        setDisabled(false);
        return response ?? {resultFlag: false};
    }, [canEditTechnicalFiles, technicalFile.id])

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={inAddMode ? 'md' : 'md'}
                loading={disabled}
                disableEscapeKeyDown={disabled}
                disableBackdropClick={disabled}
                className={'bound-dialog manage-product-technical-file-assigned-items-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {
                            inAddMode
                                ? "Assign new Items"
                                : "Assigned Items"
                        }
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                {
                    !inAddMode
                        ? <AssignedItemsList
                            technicalFileId={technicalFile?.id}
                            setInAddMode={setInAddMode}
                            assign={assignItemToTechnicalFile}
                            unAssign={unAssignItemsFromTechnicalFile}
                        />
                        : <AddAssignedItems
                            industryId={industryId}
                            setInAddMode={setInAddMode}
                            assign={assignItemToTechnicalFile}
                        />
                }
            </Dialog>
        </>
    )
}

export default ManageProductTechnicFilesAssignedItemsDialog;
